import axios from "axios";
import config from "@Config/index";

export interface TravelDurations {
  driving?: string;
  transit?: string;
  walking?: string;
  bicycling?: string;
}

const ZERO_DURATION = "0 min";

/** 1 mile = 1.60934 km — used to convert a per-km rate into a per-mile rate. */
const KM_PER_MILE = 1.60934;

/** Extracts the leading numeric mile value from a string like "2.5 mi" or "1.26 mi". */
export const parseMilesFromText = (text?: string | null): number | null => {
  if (!text) return null;
  const match = text.match(/([\d.]+)\s*mi/i);
  if (!match) return null;
  const value = parseFloat(match[1]);
  return Number.isFinite(value) ? value : null;
};

/**
 * Converts a per-km rate (as returned by cost_of_living.transportation.range)
 * into a per-mile rate: price for 1 mile = price for 1 km × km-per-mile.
 */
export const kmRateToMileRate = (ratePerKm: number): number => ratePerKm * KM_PER_MILE;

/**
 * Computes the rideshare fare range for an actual trip distance, given the
 * backend's per-1km min/max rate. The rate is first converted to a per-mile
 * rate, then scaled by the trip's distance in miles.
 */
export const calculateRideshareFareRange = (
  minPerKm: number,
  maxPerKm: number,
  distanceMiles: number,
): { min: number; max: number } => ({
  min: kmRateToMileRate(minPerKm) * distanceMiles,
  max: kmRateToMileRate(maxPerKm) * distanceMiles,
});

/**
 * Extracts the property→city-centre distance (in miles) from the pre-computed
 * destination_distances array. Any mode's entry carries the same physical
 * distance, so the first one with a readable distance.text is used.
 */
export const getCityCentreDistanceMiles = (destinationDistances: any): number | null => {
  if (!Array.isArray(destinationDistances)) return null;
  for (const entry of destinationDistances) {
    const dataObj = Array.isArray(entry) ? entry[3] : entry;
    const distanceText = dataObj?.data?.distance?.text as string | undefined;
    const miles = parseMilesFromText(distanceText);
    if (miles !== null) return miles;
  }
  return null;
};

/**
 * The distance API rejects a request whose origin and destination are the same
 * point with "origins and destinations cannot be same". For our purposes that means
 * the property IS the destination — a genuine 0-minute travel time, not missing data.
 * This checks the live API response body (params: origins == destinations).
 */
const isSameLocationErrorResponse = (data: any): boolean => {
  const message = data?.data?.message ?? data?.message;
  return typeof message === "string" && /cannot be same/i.test(message);
};

/**
 * Fetches travel durations from property to destination for all 4 modes.
 * Uses origins=property, destinations=target (matching the map section's calculateDistance).
 * Returns duration text strings (e.g. "14 mins").
 */
export const fetchDestinationDistances = async (
  propertyLat: number,
  propertyLng: number,
  destinationLat: number,
  destinationLng: number,
): Promise<TravelDurations> => {
  const modes = ["driving", "transit", "walking", "bicycling"] as const;
  const results: TravelDurations = {};

  const promises = modes.map(async (mode) => {
    try {
      const { data } = await axios.get(`${config.API_URL}/api/v0/regions/distances/fetch`, {
        params: {
          origins: `${propertyLat},${propertyLng}`,
          destinations: `${destinationLat},${destinationLng}`,
          mode,
          units: "imperial",
        },
      });
      const result = data?.data?.data;
      if (result?.status === "OK") {
        results[mode] = result.duration?.text || "";
      } else if (isSameLocationErrorResponse(data)) {
        // Property is the destination → genuine 0-minute travel time.
        results[mode] = ZERO_DURATION;
      }
    } catch {
      // Silently skip failed mode
    }
  });

  await Promise.all(promises);
  return results;
};

/**
 * Each destination_distances entry is a tuple: [ok: boolean, code, message, dataObj].
 * The backend rejects a request whose origin and destination are the same point with
 * ok=false + "origins and destinations cannot be same". For our purposes that means
 * the property IS the destination — a genuine 0-minute travel time, not missing data.
 */
const isSameLocationError = (entry: any): boolean =>
  Array.isArray(entry) &&
  entry[0] === false &&
  typeof entry[2] === "string" &&
  /cannot be same/i.test(entry[2]);

/**
 * Parses city centre durations from the pre-computed destination_distances array.
 * This data is already in the Redux store from the inventory fetch.
 */
/** Resolve a single mode's duration text from a pre-computed distance tuple. */
const resolveModeDuration = (entry: any): string | undefined => {
  if (isSameLocationError(entry)) return ZERO_DURATION;
  const dataObj = Array.isArray(entry) ? entry[3] : entry;
  if (dataObj?.data?.status === "OK") {
    return dataObj.data.duration?.text || "";
  }
  return undefined;
};

export const parseCityCentreDistances = (destinationDistances: any): TravelDurations => {
  if (
    !destinationDistances ||
    !Array.isArray(destinationDistances) ||
    destinationDistances.length < 3
  ) {
    return {};
  }

  const results: TravelDurations = {};

  // Index 0 = walking, 1 = driving, 2 = transit (standard order from backend)
  const walking = resolveModeDuration(destinationDistances[0]);
  const driving = resolveModeDuration(destinationDistances[1]);
  const transit = resolveModeDuration(destinationDistances[2]);

  if (walking !== undefined) results.walking = walking;
  if (driving !== undefined) results.driving = driving;
  if (transit !== undefined) results.transit = transit;

  return results;
};

/**
 * Extracts city centre coordinates from the destination_distances array.
 * Each entry has a request object with `origins` (property) and `destinations` (city centre).
 * We want the city centre coords (the `destinations` field in the pre-computed request).
 */
export const getCityCentreCoords = (
  destinationDistances: any,
): { lat: number; lng: number } | null => {
  if (
    !destinationDistances ||
    !Array.isArray(destinationDistances) ||
    destinationDistances.length < 1
  ) {
    return null;
  }

  // Extract from the request.destinations field of any entry
  const firstEntry = destinationDistances[0];
  if (!Array.isArray(firstEntry) || firstEntry.length < 4) return null;

  const request = firstEntry[3]?.request;
  if (request?.destinations) {
    const parts = request.destinations.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    }
  }

  return null;
};

/**
 * Fetches only the bike duration for city centre.
 * Uses the same direction as the pre-computed destination_distances:
 * origins = property, destinations = city centre.
 */
export const fetchCityCentreBike = async (
  propertyLat: number,
  propertyLng: number,
  cityCentreLat: number,
  cityCentreLng: number,
): Promise<string | undefined> => {
  try {
    const { data } = await axios.get(`${config.API_URL}/api/v0/regions/distances/fetch`, {
      params: {
        origins: `${propertyLat},${propertyLng}`,
        destinations: `${cityCentreLat},${cityCentreLng}`,
        mode: "bicycling",
        units: "imperial",
      },
    });
    const result = data?.data?.data;
    if (result?.status === "OK") {
      return result.duration?.text || undefined;
    }
    if (isSameLocationErrorResponse(data)) {
      return ZERO_DURATION;
    }
  } catch {
    // Silently skip
  }
  return undefined;
};
